import React, { useEffect, useState } from 'react'
import Link from '../../Core/Link'
import { styled } from 'linaria/react'
import { ReactComponent as ArrowIcon } from '../../../svg/ArrowBtn.svg'
import { removeAlternativeString } from '../../Core/Util/Helpers'

const ArrowBtn = styled.button`
   position: relative;
   display: flex;
   flex-direction: row;
   background: none;
   border: none;
   cursor: pointer;
   margin: 0px;
   padding: 2px;
   .arrow-icon {
      cursor: pointer;
   }
   .closed {
      transform: ${props =>
         props.isRootLevelComp ? 'rotate(0deg)' : 'rotate(-90deg)'};
      transition: transform 0.12s ease;
   }
   .opened {
      transform: rotate(-180deg);
      transition: transform 0.12s ease;
   }
   &:hover {
      opacity: 0.5;
   }
`
const List = styled.ul`
   list-style-type: none;
   margin: 0;
   padding: 0;
   /* min-height: 28px; */
`

const CategoryItem = styled.li`
   list-style-type: none;
   margin: 0;
   color: black;
   display: flex;
   justify-content: space-between;
   align-items: center;
   min-height: 28px;
   border-top: 1px solid #e0e0e0;
   /* border-bottom: 1px solid #e0e0e0; */
   padding-left: ${props =>
      props.internalLevelDepth > 1
         ? `clamp(0px, ${props.internalLevelDepth * 4}%, ${
              props.internalLevelDepth * 20
           }px)`
         : '0'};
`

const LinkItem = styled('span')`
   font-family: var(--font-industry);
   color: black;
   font-weight: ${props => (props.isSelected ? '700' : '500')};
   padding: 11px 0;
   flex-grow: 1;
   display: flex;
   align-items: center;
   /* &:hover {
      opacity: 0.7;
   } */
`

const RootSection = styled.li`
   cursor: pointer;
   list-style-type: none;
   margin: 0;
   padding: 11px 0;
   min-height: 51px;
   /* margin-bottom: 16px; */
   display: flex;
   justify-content: space-between;
   align-items: center;
   /* border-bottom: 1px solid #e0e0e0; */
`

const Heading = styled.h4`
   font-size: 1.15rem;
   margin: 0;
   font-weight: ${props => (props.isSelected ? 'bold' : 'normal')};
`

// This function recursively finds the path to the selected category
const findPathToCategory = (category, selectedCategoryId) => {
   if (category?.id == selectedCategoryId) {
      return [category?.id]
   }

   if (!category?.subCategories) {
      return null
   }

   for (const subCategory of category?.subCategories) {
      const path = findPathToCategory(subCategory, selectedCategoryId)
      if (path) {
         return [category?.id, ...path]
      }
   }

   return null
}

const CategoryTree = ({
   rootCategoryTitle = undefined,
   category,
   selectedCategoryId,
   isRootLevelComp = false,
   initiallyExpanded = false,
   internalLevelDepth = 0,
   hideLevel2Sibblings = false,
}) => {
   const [expanded, setExpanded] = useState(initiallyExpanded)

   useEffect(() => {
      const newPath = findPathToCategory(category, selectedCategoryId) || []
      // Open tree path to selected category including subcategories
      if (
         newPath.includes(category?.id) &&
         category?.subCategories?.length > 1
      ) {
         setExpanded(true)
      } else if (!initiallyExpanded) {
         setExpanded(false)
      }
   }, [category, selectedCategoryId, initiallyExpanded])

   const handleToggle = () => {
      setExpanded(!expanded)
   }

   if (category?.isHidden === true && !isRootLevelComp) {
      return null
   }

   const isSelected = category?.id == selectedCategoryId
   const hasSubCategories = category?.subCategories?.length > 0

   return (
      <List
         style={{
            ...(expanded && isRootLevelComp
               ? { paddingBottom: '0.75rem' }
               : {}),
         }}
      >
         {!isRootLevelComp ? (
            <CategoryItem
               isSubCategory={true}
               internalLevelDepth={internalLevelDepth}
            >
               <Link to={category?.path} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <LinkItem 
                     isSelected={isSelected}
                     internalLevelDepth={internalLevelDepth}
                  >
                     <span
                        style={{ whiteSpace: 'normal' }}
                        className="link link--vb-highlight-underline"
                     >
                        {removeAlternativeString(category?.name)}
                     </span>
                  </LinkItem>
                  {hasSubCategories && (
                     <ArrowBtn
                        onClick={e => {
                           e.preventDefault();
                           handleToggle();
                        }}
                        aria-label="Toggle Subcategories"
                     >
                        <ArrowIcon
                           width={22}
                           height={22}
                           className={`arrow-icon ${
                              expanded ? 'opened' : 'closed'
                           }`}
                        />
                     </ArrowBtn>
                  )}
               </Link>
            </CategoryItem>
         ) : (
            <RootSection onClick={handleToggle}>
               <Heading isSelected={isSelected}>
                  {rootCategoryTitle ?? removeAlternativeString(category?.name)}
               </Heading>
               {hasSubCategories && (
                  <ArrowBtn
                     // onClick={handleToggle}
                     isRootLevelComp={isRootLevelComp}
                     aria-label="Toggle Subcategories"
                  >
                     <ArrowIcon
                        className={`arrow-icon ${
                           expanded ? 'opened' : 'closed'
                        }`}
                        strokeWidth={2}
                     />
                  </ArrowBtn>
               )}
            </RootSection>
         )}
         {expanded && category?.subCategories && (
            <List style={{ paddingLeft: '0px' }}>
               {category?.subCategories.filter(sub => !hideLevel2Sibblings || (sub.level > 2 || sub.id == selectedCategoryId))?.map((subCategory, index) => (
                  <CategoryTree
                     key={subCategory?.id}
                     category={subCategory}
                     selectedCategoryId={selectedCategoryId}
                     isRootLevelComp={false}
                     internalLevelDepth={internalLevelDepth + 1}
                  />
               ))}
            </List>
         )}
      </List>
   )
}

export default CategoryTree
